"use client";

import Image from "next/image";
import { motion, useReducedMotion } from "framer-motion";
import { useTheme } from "@/lib/theme/them-provider";
import { useLocale } from "@/lib/locale/locale-provider";
import type { GiftItem, GiftStatus } from "@/lib/sections/gifts.types";

type Props = {
  gift: GiftItem;
  status: GiftStatus;
  currency: string;
  onSelect: () => void;
  index?: number;
};

export function GiftCard({ gift, status, currency, onSelect, index = 0 }: Props) {
  const { theme } = useTheme();
  const locale = useLocale();
  const prefersReducedMotion = useReducedMotion();

  const actionLabel =
    locale === "fr"
      ? "Choisir"
      : locale === "en"
        ? "Select"
        : "Auswaehlen";
  const fallbackSubtitle =
    locale === "fr"
      ? "Une attention delicate pour le debut de notre vie a deux."
      : locale === "en"
        ? "A thoughtful contribution for the beginning of our life together."
        : "Ein besonderer Beitrag fuer unseren gemeinsamen Start.";

  const subtitle = gift.description?.trim() || fallbackSubtitle;

  return (
    <motion.button
      type="button"
      onClick={onSelect}
      data-gift-card-id={gift.id}
      initial={prefersReducedMotion ? false : { opacity: 0, y: 24 }}
      whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={
        prefersReducedMotion
          ? undefined
          : {
              duration: 0.55,
              ease: [0.22, 1, 0.36, 1],
              delay: index * 0.08,
            }
      }
      whileHover={
        prefersReducedMotion
          ? undefined
          : {
              scale: 1.03,
              y: -5,
              boxShadow: "0 24px 50px rgba(32, 24, 16, 0.16)",
            }
      }
      whileTap={prefersReducedMotion ? undefined : { scale: 0.985 }}
      style={{
        width: "100%",
        padding: 0,
        height: "clamp(232px, 34vw, 360px)",
        position: "relative",
        border: `1px solid ${theme.colors.border}`,
        borderRadius: 36,
        overflow: "hidden",
        background: theme.colors.surface,
        boxShadow: "0 20px 44px rgba(32, 24, 16, 0.11)",
        cursor: "pointer",
        textAlign: "left",
        transition: "box-shadow 220ms ease",
      }}
    >
      <Image
        src={gift.image}
        alt={gift.title}
        fill
        sizes="(max-width: 1023px) 50vw, 420px"
        style={{
          objectFit: "cover",
          filter: "none",
        }}
      />

      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.08) 4%, rgba(0,0,0,0.38) 58%, rgba(0,0,0,0.82) 100%)",
        }}
      />

      <div
        style={{
          position: "absolute",
          left: 16,
          right: 16,
          bottom: 14,
          display: "grid",
          gap: 8,
          color: "#ffffff",
        }}
      >
        <div style={{ display: "grid", gap: 4 }}>
          <h3
            style={{
              margin: 0,
              fontFamily: theme.fonts.heading,
              fontSize: "clamp(1.02rem, 2.2vw, 1.7rem)",
              lineHeight: 1.08,
            }}
          >
            {gift.title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "clamp(0.66rem, 1vw, 0.88rem)",
              lineHeight: 1.42,
              color: "rgba(255,255,255,0.9)",
            }}
          >
            {subtitle}
          </p>
        </div>

        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            borderRadius: 999,
            background: "linear-gradient(180deg, #d0aa53 0%, #c49737 100%)",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "clamp(0.68rem, 1.05vw, 0.92rem)",
            lineHeight: 1.1,
            padding: "9px 12px",
          }}
        >
          {actionLabel}
        </span>
      </div>
    </motion.button>
  );
}
